import { useEffect, useState } from 'react';
import { getAlerts, acknowledgeAlert, resolveAlert } from '../api/alerts-api';
import { useDataCache } from '@/store/data-cache-context';
import { useOrg } from '@/store/org-context';
import { AlertStatus } from '@/types/api';
import type { Alert } from '@/types/api';

interface UseAlertsOptions {
  propertyId?: string;
  deviceId?: string;
}

export function useAlerts({ propertyId, deviceId }: UseAlertsOptions = {}) {
  const {
    getAlerts: getCachedAlerts,
    setAlerts: setCachedAlerts,
  } = useDataCache();
  const { activeOrgId } = useOrg();

  const [allAlerts, setAllAlerts] = useState<Alert[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState<AlertStatus | ''>(AlertStatus.Active);

  useEffect(() => {
    loadAlerts();
  }, [activeOrgId, propertyId]); // eslint-disable-line react-hooks/exhaustive-deps

  async function loadAlerts() {
    // Cache only stores the org-wide list. When a property filter is set,
    // bypass the cache and fetch fresh — serving stale all-org data while
    // the user expects a per-property view would be misleading.
    if (!propertyId) {
      const cached = getCachedAlerts();
      if (cached) {
        setAllAlerts(cached);
        setLoading(false);
        return;
      }
    }
    setLoading(true);
    try {
      const response = await getAlerts(propertyId ? { propertyId } : {});
      const data = response.data || [];
      if (!propertyId) setCachedAlerts(data);
      setAllAlerts(data);
    } catch (error) {
      console.error('Failed to load alerts:', error);
    } finally {
      setLoading(false);
    }
  }

  // Device filter is applied client-side. The alerts list is small per
  // org and `/alerts` doesn't accept a deviceId filter today; once it
  // does, swap this for a server-side param.
  const scoped = deviceId
    ? allAlerts.filter(a => a.deviceId === deviceId)
    : allAlerts;

  // Resolved alerts older than this fall off the page entirely — the
  // table doesn't render them and the Resolved tile count excludes
  // them. Mirrors how an installer thinks about resolved alerts:
  // anything older than a week is "history" and shouldn't drown out
  // recent activity. Active and Acknowledged are not time-bounded.
  const RESOLVED_RECENT_MS = 7 * 24 * 60 * 60 * 1000;
  const isRecentResolved = (a: Alert) => {
    if (a.status !== AlertStatus.Resolved) return false;
    if (!a.resolvedAt) return false;
    return Date.now() - new Date(a.resolvedAt).getTime() < RESOLVED_RECENT_MS;
  };

  // Counts are computed from `scoped` (property + device filters apply,
  // but NOT the status filter). Computing them off the already-status-
  // filtered list — as the page used to do — meant clicking a status
  // tab zeroed out the other tile counts because there were no rows of
  // that status in the filtered set. Keep the counts stable across tab
  // changes so the strip always shows the same totals for the page's
  // current scope.
  const activeCount = scoped.filter(a => a.status === AlertStatus.Active).length;
  const acknowledgedCount = scoped.filter(a => a.status === AlertStatus.Acknowledged).length;
  const resolvedCount = scoped.filter(isRecentResolved).length;

  const alerts = filterStatus === AlertStatus.Resolved
    ? scoped.filter(isRecentResolved)
    : filterStatus
      ? scoped.filter(a => a.status === filterStatus)
      : scoped;

  async function handleAcknowledge(alert: Alert) {
    const prevAlerts = allAlerts;
    const updated = allAlerts.map(a =>
      a.id === alert.id
        ? { ...a, status: AlertStatus.Acknowledged }
        : a
    );
    setAllAlerts(updated);
    setCachedAlerts(updated);
    try {
      await acknowledgeAlert(alert.id);
    } catch (error) {
      setAllAlerts(prevAlerts);
      setCachedAlerts(prevAlerts);
      console.error('Failed to acknowledge alert:', error);
    }
  }

  async function handleResolve(alert: Alert) {
    const prevAlerts = allAlerts;
    const updated = allAlerts.map(a =>
      a.id === alert.id
        ? { ...a, status: AlertStatus.Resolved, resolvedAt: new Date().toISOString() }
        : a
    );
    setAllAlerts(updated);
    setCachedAlerts(updated);
    try {
      await resolveAlert(alert.id);
    } catch (error) {
      setAllAlerts(prevAlerts);
      setCachedAlerts(prevAlerts);
      console.error('Failed to resolve alert:', error);
    }
  }

  return {
    alerts,
    loading,
    filterStatus,
    setFilterStatus,
    activeCount,
    acknowledgedCount,
    resolvedCount,
    handleAcknowledge,
    handleResolve,
  };
}
